/*
 * Geraetekategorien (23.10.2026): was ein Geraet fachlich ist, von Hand
 * gesetzt (DeviceEditModal), auf der Karte zu einem Symbol je Anlage
 * zusammengefasst (MapPage). Werte wie das C#-Enum DeviceCategory.
 *
 * Icons als SVG-Innenleben im 24er-Raster und Stil der HvacIcons (Kontur
 * 1,5, currentColor). Als Text, weil die Kartenmarker HTML-Strings sind.
 */

export type DeviceCategory =
  | 'HeatPump' | 'Boiler' | 'DistrictHeating' | 'SolarThermal' | 'Photovoltaic'
  | 'Battery' | 'Ventilation' | 'Cooling' | 'RoomControl' | 'Meter' | 'Other';

export interface CategoryInfo {
  key: DeviceCategory;
  label: string;
  /** Kleiner = wichtiger fuer das Kartensymbol (wie DeviceCategories.Rank). */
  rank: number;
  variants?: { key: string; label: string }[];
}

export const DEVICE_CATEGORIES: CategoryInfo[] = [
  { key: 'HeatPump', label: 'Wärmepumpe', rank: 0, variants: [
    { key: 'Air', label: 'Luft' }, { key: 'Brine', label: 'Sole / Erdreich' }, { key: 'Water', label: 'Wasser' }] },
  { key: 'Boiler', label: 'Kessel', rank: 1, variants: [
    { key: 'Gas', label: 'Gas' }, { key: 'Oil', label: 'Öl' }, { key: 'Biomass', label: 'Pellets / Holz' }] },
  { key: 'DistrictHeating', label: 'Fernwärme', rank: 2 },
  { key: 'SolarThermal', label: 'Solarthermie', rank: 3 },
  { key: 'Ventilation', label: 'Lüftung', rank: 10, variants: [
    { key: 'HeatRecovery', label: 'mit Wärmerückgewinnung' }, { key: 'NoHeatRecovery', label: 'ohne Wärmerückgewinnung' }] },
  { key: 'Cooling', label: 'Klima / Kälte', rank: 11 },
  { key: 'Photovoltaic', label: 'Photovoltaik', rank: 20 },
  { key: 'Battery', label: 'Batteriespeicher', rank: 21 },
  { key: 'RoomControl', label: 'Raumregelung', rank: 30 },
  { key: 'Meter', label: 'Zähler / Energie', rank: 31, variants: [
    { key: 'Electricity', label: 'Strom' }, { key: 'Heat', label: 'Wärme' }, { key: 'Water', label: 'Wasser' }] },
  { key: 'Other', label: 'Sonstiges', rank: 40 },
];

export function categoryInfo(key: string | null | undefined): CategoryInfo | undefined {
  return DEVICE_CATEGORIES.find((c) => c.key === key);
}

export function variantLabel(category: string | null | undefined, variant: string | null | undefined): string | null {
  if (!variant) return null;
  return categoryInfo(category)?.variants?.find((v) => v.key === variant)?.label ?? null;
}

const P = (d: string) => `<path d="${d}"/>`;

/** SVG-Innenleben je Icon-Schluessel; Schluessel = Kategorie oder Kategorie:Auspraegung. */
const ICONS: Record<string, string> = {
  HeatPump: '<rect x="4" y="5" width="16" height="14" rx="1"/><circle cx="11" cy="12" r="3.5"/>' + P('M11 8.5 V15.5 M7.5 12 H14.5') + P('M17 9 V15'),
  'HeatPump:Air': P('M3 8.5 H13 A2.5 2.5 0 1 0 10.5 6') + P('M3 12.5 H17 A2.5 2.5 0 1 1 14.5 15') + P('M3 16.5 H9'),
  'HeatPump:Brine': P('M4 7 L12 4 L20 7 L12 10 Z') + P('M4 11.5 L12 14.5 L20 11.5') + P('M4 16 L12 19 L20 16'),
  'HeatPump:Water': P('M12 3.5 C12 3.5 6.5 10 6.5 14 A5.5 5.5 0 0 0 17.5 14 C17.5 10 12 3.5 12 3.5 Z'),
  Boiler: P('M12 3 C14 7 18 9 18 14 A6 6 0 0 1 6 14 C6 11 8 9 9 7 C10 9 11 10 12 10 C12 8 11 5 12 3 Z'),
  DistrictHeating: P('M4 11 L12 4 L20 11 V20 H4 Z') + P('M8 15 H15 M13 13 L15 15 L13 17'),
  SolarThermal: '<circle cx="7" cy="6.5" r="2.5"/>' + P('M7 2 V3 M2.5 6.5 H3.5 M3.8 3.3 L4.5 4') + P('M9 20 L12 11 H21 L18 20 Z') + P('M13.5 15.5 H19.5'),
  Photovoltaic: P('M3 18 L6 8 H21 L18 18 Z') + P('M4.5 13 H19.5 M11 8 L9 18 M16 8 L14 18'),
  Battery: '<rect x="3" y="7" width="16" height="10" rx="1"/>' + P('M21 10 V14') + P('M11 9 L8.5 12 H12.5 L10 15'),
  Ventilation: '<circle cx="12" cy="12" r="1.6"/>' + P('M12 10.4 C10.6 6.5 12.4 3.5 14.6 4.2 C16.8 5 15.6 9 13.2 11')
    + P('M13.4 12.9 C17.2 14.6 18.2 17.8 16.4 19 C14.6 20.2 12.2 17 12 13.6')
    + P('M10.6 12.9 C7 15 4.2 14 4.2 12 C4.2 9.8 8.2 9.8 10.6 11.2'),
  Cooling: P('M12 3 V21 M4.2 7.5 L19.8 16.5 M4.2 16.5 L19.8 7.5') + P('M9.5 4.5 L12 6.5 L14.5 4.5 M9.5 19.5 L12 17.5 L14.5 19.5'),
  RoomControl: P('M10 5 A2 2 0 0 1 14 5 V13.6 A4 4 0 1 1 10 13.6 Z') + P('M12 9 V16.5'),
  Meter: P('M4.5 17 A7.5 7.5 0 1 1 19.5 17 Z') + P('M12 13.5 L15.5 9.5'),
  Other: P('M4 8 L12 4 L20 8 V16 L12 20 L4 16 Z') + P('M4 8 L12 12 L20 8 M12 12 V20'),
  /** Keine Kategorie eingetragen. */
  Plant: P('M4 20 V9 L10 6 V20') + P('M10 10 H20 V20') + P('M3 20 H21') + P('M13 13 H17 M13 16.5 H17'),
  /** Servicezugang offen (kleiner Punkt am Marker). */
  Service: P('M14.5 5.5 A4 4 0 0 0 9.8 10.6 L4.5 15.9 L8.1 19.5 L13.4 14.2 A4 4 0 0 0 18.5 9.5 L16 12 L12 8 Z'),
};

/** Icon-Schluessel fuer ein Geraet: mit Auspraegung, wo es dafuer ein eigenes gibt. */
export function iconKey(category: string | null | undefined, variant?: string | null): string {
  if (!category) return 'Plant';
  const specific = variant ? `${category}:${variant}` : null;
  if (specific && ICONS[specific]) return specific;
  return ICONS[category] ? category : 'Other';
}

export function iconSvg(key: string, size = 20, extraClass = ''): string {
  const inner = ICONS[key] ?? ICONS.Other;
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"`
    + ` stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"${extraClass ? ` class="${extraClass}"` : ''}>${inner}</svg>`;
}
